import type {
  AskZotAbout,
  MembershipProject,
  ZotMessage,
  ZotRoutedTool,
} from "@zworks/console-contracts";
import { ArrowRightLeft, Check, LoaderCircle, Lock, Sparkles } from "lucide-react";
import * as React from "react";
import { Link } from "react-router";
import { ApiError, confirmZot, readZotStream, renameZotThread } from "@/lib/api";
import { NotConnectedState } from "@/components/states";
import { ZotComposer } from "@/components/zot-composer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

function citationPath(message: ZotMessage["citations"][number]): string {
  const id = message.ref.split("/").at(-1) ?? "";
  switch (message.kind) {
    case "run":
      return `/runs/${id}`;
    case "flow":
      return `/flows/${id}/edit`;
    case "ledger-entry":
    case "wallet":
      return "/usage";
    case "member":
      return "/settings/members";
    case "artefact":
      return "/settings/artifacts";
    case "approval":
      return `/approvals/${id}`;
    case "audit-entry":
      return "/audit";
    case "repository":
      return "/settings/repositories";
    case "work-item":
      return `/work-items?key=${encodeURIComponent(id)}`;
    case "file":
      return "/settings/artifacts";
  }
}

/** Which tool the router chose, with a one-click switch. The composer stays free of pickers. */
function RoutingLine({
  message,
  onSwitch,
}: {
  message: ZotMessage;
  onSwitch: (tool: ZotRoutedTool) => void;
}) {
  const routing = message.routing;
  if (!routing || message.role !== "assistant") return null;
  const toolLabel = routing.tool === "answer" ? "general answer" : `/${routing.tool}`;
  const dispatch = routing.dispatch ?? "off";
  const verb =
    dispatch === "executed" ? "Via" : dispatch === "refused" ? "Could not run" : "Routed";
  const depth = routing.analysisDepth ?? "standard";
  return (
    <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <span aria-label={`${verb} for ${toolLabel}`}>
        {verb} {toolLabel} · {routing.modelTier} model
        {routing.byok ? " · your key" : null}
        {routing.suggestedCap !== null
          ? ` · suggested cap ${routing.suggestedCap.toLocaleString("en-GB")}`
          : null}
        {` · ${depth} analysis`}
      </span>
      {routing.alternatives.slice(0, 2).map((tool) => (
        <button
          key={tool}
          type="button"
          className="inline-flex items-center gap-0.5 underline underline-offset-2 hover:text-foreground"
          aria-label={`Try with ${tool === "answer" ? "a general answer" : `/${tool}`} instead`}
          onClick={() => onSwitch(tool)}
        >
          <ArrowRightLeft className="size-3" aria-hidden />
          {tool === "answer" ? "answer" : `/${tool}`}
        </button>
      ))}
    </p>
  );
}

function ProposalBlock({
  project,
  message,
  onConfirmed,
}: {
  project: MembershipProject | null;
  message: ZotMessage;
  onConfirmed: (updated: ZotMessage) => void;
}) {
  const [confirming, setConfirming] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const proposal = message.proposal;
  if (!proposal) return null;
  if (message.proposalState === "confirmed") {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Badge variant="success">
          <Check className="mr-1 size-3" />
          Confirmed
        </Badge>
        {proposal.action.kind === "start-agent-task" ? (
          <Link
            className="text-sm text-accent-text underline underline-offset-2"
            to="/runs?type=agent"
          >
            Open Runs to see the reserved cap
          </Link>
        ) : null}
        {proposal.action.kind === "build-evidence-pack" ? (
          <Link className="text-sm text-accent-text underline underline-offset-2" to="/runs">
            Open Runs to download the evidence pack
          </Link>
        ) : null}
        {proposal.action.kind === "draft-workflow" ? (
          message.draftFlowId ? (
            <Link
              className="text-sm text-accent-text underline underline-offset-2"
              to={`/flows/${message.draftFlowId}/edit`}
            >
              Open the draft in the designer
            </Link>
          ) : (
            <Link
              className="text-sm text-accent-text underline underline-offset-2"
              to="/studio/workflows"
            >
              Open Workflows in Studio
            </Link>
          )
        ) : null}
      </div>
    );
  }
  const confirmLabel =
    proposal.action.kind === "start-agent-task"
      ? "Review and confirm"
      : proposal.action.kind === "draft-workflow"
        ? "Save draft"
        : "Confirm change";
  return (
    <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-3">
      <p className="text-sm font-medium">Proposal: {proposal.summary}</p>
      {proposal.action.kind === "change-usage-limit" ? (
        <div className="mt-2 rounded border bg-card p-2 text-sm">
          <p className="font-medium">
            {proposal.action.input.field === "monthly-limit" ? "Monthly limit" : "Per-run cap"}
          </p>
          <p className="text-muted-foreground">
            {proposal.action.input.expectedValue.toLocaleString("en-GB")} →{" "}
            {proposal.action.input.requestedValue.toLocaleString("en-GB")} credits
          </p>
          <p className="mt-1 text-muted-foreground">
            Only the project owner can confirm this change.
          </p>
        </div>
      ) : null}
      {proposal.action.kind === "start-agent-task" ? (
        <div className="mt-2 rounded border bg-card p-2 text-sm">
          <p className="font-medium">New task</p>
          <p className="text-muted-foreground">
            {proposal.action.input.estimateMinCredits !== undefined &&
            proposal.action.input.estimateMaxCredits !== undefined
              ? `Estimated ${proposal.action.input.estimateMinCredits.toLocaleString("en-GB")}–${proposal.action.input.estimateMaxCredits.toLocaleString("en-GB")} credits`
              : "Estimated cost: unknown until the backend scopes the work"}
          </p>
          <p className="text-muted-foreground">
            {proposal.action.input.byok
              ? "Your model key is used, so no project credits are reserved."
              : proposal.action.input.creditCap !== undefined
                ? `Spend cap: ${proposal.action.input.creditCap.toLocaleString("en-GB")} credits, reserved only after you confirm`
                : "Spend cap: the project per-task cap, reserved only after you confirm"}
          </p>
          <p className="mt-1 text-muted-foreground">
            Nothing starts before you confirm. The cap is shown on the run once reserved.
          </p>
        </div>
      ) : null}
      {proposal.action.kind === "create-work-item" ? (
        <div className="mt-2 rounded border bg-card p-2 text-sm">
          <p className="font-medium">
            {proposal.action.input.key}: {proposal.action.input.title}
          </p>
          <p className="text-muted-foreground">
            New work item · {proposal.action.input.status} · {proposal.action.input.priority}
          </p>
          {proposal.action.input.description ? (
            <p className="mt-1 text-muted-foreground">{proposal.action.input.description}</p>
          ) : null}
        </div>
      ) : null}
      {proposal.action.kind === "draft-workflow" ? (
        <div className="mt-2 rounded border bg-card p-2 text-sm">
          <p className="flex flex-wrap items-center gap-2 font-medium">
            {proposal.action.input.name}
            <Badge variant="outline">Draft</Badge>
          </p>
          <ol className="mt-1 list-inside list-decimal text-muted-foreground">
            {proposal.action.input.steps.map((step, index) => (
              <li key={`${index}-${step.name}`}>
                {step.name}
                {step.gate || step.kind === "approval" ? " (gate)" : ""}
              </li>
            ))}
          </ol>
          <p className="mt-1 text-muted-foreground">
            Saved as a draft when you confirm. It binds no tool, skill, decider, or spend cap until
            you set them in the designer, and it is not published.
          </p>
        </div>
      ) : null}
      {proposal.action.kind === "build-evidence-pack" ? (
        <div className="mt-2 rounded border bg-card p-2 text-sm">
          <p className="font-medium">Evidence pack</p>
          <p className="text-muted-foreground">
            The signed pack for the run is built only after you confirm, then saved as an export in
            this chat. Audits and signatures are verified, never altered.
          </p>
        </div>
      ) : null}
      {proposal.binding ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Bound to revision {proposal.binding.revision}. {proposal.binding.observed}
        </p>
      ) : null}
      <ul className="mt-1 list-inside list-disc text-sm text-muted-foreground">
        {proposal.changes.map((change, index) => (
          <li key={`${index}-${change}`}>{change}</li>
        ))}
      </ul>
      <Button
        className="mt-3"
        size="sm"
        disabled={
          confirming ||
          !project ||
          (proposal.action.kind === "change-usage-limit" && project.role !== "owner")
        }
        onClick={() => {
          if (!project) return;
          setConfirming(true);
          setError(null);
          confirmZot(project.id, { messageId: message.id }).then(
            (updated) => {
              setConfirming(false);
              onConfirmed({ ...updated, proposalState: "confirmed" });
            },
            (cause: unknown) => {
              setConfirming(false);
              setError(
                cause instanceof ApiError
                  ? (cause.problem?.detail ?? cause.message)
                  : "The proposal could not be confirmed.",
              );
            },
          );
        }}
      >
        {confirming ? <LoaderCircle className="mr-2 size-4 animate-spin" /> : null}
        {confirmLabel}
      </Button>
      {error ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function ZotChat({
  project,
  pageContext,
  className,
  threadId: controlledThreadId,
  onThreadChange,
  onInitialDraftSubmit,
  autoTitle = false,
  submitInitialDraft = false,
  initialDraft = "",
  initialMessages = [],
  hideHeader = false,
  about = null,
}: {
  project: MembershipProject | null;
  pageContext: string;
  className?: string;
  threadId?: string | null;
  onThreadChange?: (threadId: string) => void;
  onInitialDraftSubmit?: () => void;
  autoTitle?: boolean;
  submitInitialDraft?: boolean;
  initialDraft?: string;
  initialMessages?: ZotMessage[];
  hideHeader?: boolean;
  /** Object from the Ask button, sent with each message so the server can authorise it. */
  about?: AskZotAbout | null;
}) {
  const [internalThreadId, setInternalThreadId] = React.useState<string | null>(null);
  const threadId = controlledThreadId !== undefined ? controlledThreadId : internalThreadId;
  const [messages, setMessages] = React.useState<ZotMessage[]>(initialMessages);
  const [draft, setDraft] = React.useState(initialDraft);
  const [error, setError] = React.useState<string | null>(null);
  const [notConfigured, setNotConfigured] = React.useState(false);
  const [thinking, setThinking] = React.useState(false);
  const [streamedText, setStreamedText] = React.useState("");
  const [refusalReason, setRefusalReason] = React.useState<string | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const initialDraftSubmitted = React.useRef(false);
  const listRef = React.useRef<HTMLDivElement>(null);
  const streamingActive = thinking || streamedText.length > 0;

  React.useEffect(() => {
    listRef.current?.scrollTo?.({ top: listRef.current.scrollHeight });
  }, [messages, streamedText, thinking]);

  React.useEffect(() => () => abortRef.current?.abort(), []);

  function handleConfirmed(updated: ZotMessage) {
    setMessages((current) => current.map((item) => (item.id === updated.id ? updated : item)));
  }

  /** One-click tool switch: prefill the composer with the other command, keeping the question. */
  function handleSwitch(message: ZotMessage, tool: ZotRoutedTool) {
    const question = messages
      .slice(
        0,
        messages.findIndex((item) => item.id === message.id),
      )
      .reverse()
      .find((item) => item.role === "user")?.body;
    if (tool === "answer") {
      if (question) setDraft(question);
      return;
    }
    setDraft(question ? `/${tool} ${question}` : `/${tool} `);
  }

  const submit = React.useCallback(async () => {
    if (!project || !draft.trim() || streamingActive) return;
    const prompt = draft.trim();
    setDraft("");
    setError(null);
    setNotConfigured(false);
    setRefusalReason(null);
    setThinking(true);
    setStreamedText("");
    const userMessage: ZotMessage = {
      id: crypto.randomUUID(),
      threadId: threadId ?? crypto.randomUUID(),
      role: "user",
      body: prompt,
      citations: [],
      proposal: null,
      proposalState: null,
      routing: null,
    };
    setMessages((current) => [...current, userMessage]);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let accumulated = "";
    let refusal: string | null = null;
    let failed: string | null = null;
    let done: ZotMessage | null = null;
    try {
      for await (const streamEvent of readZotStream(
        project.id,
        about ? { threadId, message: prompt, about } : { threadId, message: prompt },
        controller.signal,
      )) {
        if (controller.signal.aborted) break;
        if (streamEvent.type === "token") {
          accumulated += streamEvent.text;
          setStreamedText(accumulated);
          setThinking(false);
        } else if (streamEvent.type === "refusal") {
          refusal = streamEvent.reason;
          setRefusalReason(streamEvent.reason);
        } else if (streamEvent.type === "error") {
          failed = streamEvent.detail;
        } else if (streamEvent.type === "done") {
          done = streamEvent.message;
        }
      }
    } catch (cause) {
      if (controller.signal.aborted) return;
      if (cause instanceof ApiError && cause.status === 503) {
        setNotConfigured(true);
        setError(null);
        setThinking(false);
        setStreamedText("");
        setMessages((current) => current.filter((item) => item.id !== userMessage.id));
        return;
      }
      failed =
        cause instanceof ApiError
          ? (cause.problem?.detail ?? cause.message)
          : "Zot could not answer just now.";
    }
    setThinking(false);
    setStreamedText("");
    if (failed && !done && !accumulated) {
      setError(failed);
      return;
    }
    if (done) {
      if (controlledThreadId === undefined) setInternalThreadId(done.threadId);
      onThreadChange?.(done.threadId);
      setMessages((current) => [
        ...current.filter((item) => item.id !== userMessage.id),
        userMessage,
        done as ZotMessage,
      ]);
      if (autoTitle && !threadId) {
        try {
          await renameZotThread(project.id, done.threadId, { title: prompt.slice(0, 80) });
        } catch {
          // Titles are a convenience; a failed rename never blocks the answer.
        }
      }
    } else {
      setMessages((current) => [
        ...current.filter((item) => item.id !== userMessage.id),
        userMessage,
        {
          id: crypto.randomUUID(),
          threadId: userMessage.threadId,
          role: "assistant",
          body: refusal ?? accumulated,
          citations: [],
          proposal: null,
          proposalState: null,
          routing: null,
        },
      ]);
      if (failed) setError(failed);
    }
  }, [
    about,
    autoTitle,
    controlledThreadId,
    draft,
    onThreadChange,
    project,
    streamingActive,
    threadId,
  ]);

  React.useEffect(() => {
    if (!submitInitialDraft || !project || !initialDraft.trim() || initialDraftSubmitted.current) {
      return;
    }
    // Defer until after React Strict Mode's mount-effect replay, whose cleanup aborts streams.
    const timeout = window.setTimeout(() => {
      if (initialDraftSubmitted.current) return;
      initialDraftSubmitted.current = true;
      onInitialDraftSubmit?.();
      void submit();
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [initialDraft, onInitialDraftSubmit, project, submit, submitInitialDraft]);

  return (
    <section className={cn("flex min-h-0 flex-col", className)} aria-label="Ask Zot">
      {hideHeader ? null : (
        <div className="border-b p-4">
          <div className="flex items-center gap-2">
            <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 text-accent-text">
              <Sparkles className="size-4" aria-hidden />
            </span>
            <h2 className="text-lg font-semibold">Ask Zot</h2>
            <Badge variant="secondary">Project assistant</Badge>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Scope">
            <Badge variant="outline">Project: {project ? project.name : "None"}</Badge>
            <Badge variant="outline">Page: {pageContext || "Project"}</Badge>
            <Badge variant="outline">Role: {project ? project.role : "None"}</Badge>
          </div>
          <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="size-3" aria-hidden />
            Sees this project only, read only. Zot cannot decide approvals, and it only changes
            project state after you confirm a proposal. Chats do not use credits.
          </p>
        </div>
      )}

      <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto p-4" aria-live="polite">
        {notConfigured ? (
          <NotConnectedState
            title="Ask Zot is not connected yet"
            description="No assistant is configured for this environment, so questions return 503. Connect an assistant adapter before offering answers."
          />
        ) : null}
        {messages.length === 0 && !notConfigured ? (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            Ask about this project&apos;s runs, flows, work items, or records. Do not include
            passwords, tokens, or other secrets.
          </div>
        ) : null}
        {messages.map((message) => (
          <article
            key={message.id}
            className={cn("rounded-lg border p-3", message.role === "user" && "ml-8 bg-muted/50")}
          >
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              {message.role === "user" ? "You" : "Zot"}
            </p>
            <p className="whitespace-pre-wrap text-sm leading-6">{message.body}</p>
            {message.citations.length > 0 ? (
              <ul className="mt-2 flex flex-wrap gap-2">
                {message.citations.map((citation) => (
                  <li key={`${citation.kind}:${citation.ref}`}>
                    <Link
                      className="text-xs text-accent-text underline underline-offset-2"
                      to={citationPath(citation)}
                    >
                      {citation.label}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
            <RoutingLine message={message} onSwitch={(tool) => handleSwitch(message, tool)} />
            <ProposalBlock project={project} message={message} onConfirmed={handleConfirmed} />
          </article>
        ))}
        {streamingActive ? (
          <article className="rounded-lg border p-3" aria-label="Zot is answering">
            <p className="mb-1 text-xs font-medium text-muted-foreground">Zot</p>
            {thinking && !streamedText ? (
              <p role="status" aria-label="Zot is thinking" className="flex gap-1 py-1">
                {[0, 1, 2].map((index) => (
                  <span
                    key={index}
                    aria-hidden
                    className="size-2 animate-pulse rounded-full bg-muted-foreground"
                    style={{ animationDelay: `${index * 200}ms` }}
                  />
                ))}
              </p>
            ) : (
              <p className="whitespace-pre-wrap text-sm leading-6">
                {streamedText}
                <span
                  aria-hidden
                  className="ml-0.5 inline-block size-2 animate-pulse rounded-full bg-current align-middle"
                />
              </p>
            )}
          </article>
        ) : null}
        {refusalReason ? (
          <p role="note" className="rounded-lg border p-3 text-sm text-muted-foreground">
            Zot refused part of that request: {refusalReason}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="border-t p-3"
      >
        <ZotComposer
          value={draft}
          onChange={setDraft}
          onSubmit={() => void submit()}
          disabled={!project}
          busy={streamingActive}
        />
      </form>
    </section>
  );
}
